import { useFormListValidator } from 'core';
import { User } from 'core/Api';
import { phoneNumberDisplayFormat } from 'core/Utils';
import { Grid, Select, Stack, Text, TextInput } from 'ui';
import { isNotEmpty, matches, useForm } from 'ui/Form';

import { ProfileFormType } from './editProfile.types';
import { REGEX } from './regex';

const mobileCarrierData = [
  { value: 'Alltel', label: 'Alltel' },
  { value: 'AT&T', label: 'AT&T' },
  { value: 'Boost', label: 'Boost' },
  { value: 'Sprint', label: 'Sprint' },
  { value: 'T-Mobile', label: 'T-Mobile' },
  { value: 'Virgin', label: 'Virgin' },
  { value: 'Verizon', label: 'Verizon' },
  { value: 'US Cellular', label: 'US Cellular' },
  { value: 'Bell Mobility', label: 'Bell Mobility' },
  { value: 'Rogers Wireless', label: 'Rogers Wireless' },
  { value: 'Fido', label: 'Fido' },
  { value: 'TELUS Mobility', label: 'TELUS Mobility' },
  { value: 'Virgin (Canada)', label: 'Virgin (Canada)' },
  { value: 'PC Mobile', label: 'PC Mobile' },
  { value: 'Koodo Mobile', label: 'Koodo Mobile' },
  { value: 'SaskTel', label: 'SaskTel' },
  { value: 'T-Mobile (UK)', label: 'T-Mobile (UK)' },
  { value: 'Orange (UK)', label: 'Orange (UK)' },
  { value: 'Vodafone (UK)', label: 'Vodafone (UK)' },
  { value: 'O2 (UK)', label: 'O2 (UK)' },
  { value: 'Straight Talk', label: 'Straight Talk' },
  { value: 'M1', label: 'M1' },
  { value: 'Vodafone (ZA)', label: 'Vodafone (ZA)' },
  { value: 'Nextel', label: 'Nextel' },
  { value: 'Cricket', label: 'Cricket' },
  { value: 'Cleartalk', label: 'Cleartalk' },
  { value: 'Metro PCS', label: 'Metro PCS' },
  { value: 'H2O', label: 'H2O' },
  { value: 'CSpire', label: 'CSpire' },
  { value: 'Telstra', label: 'Telstra' },
  { value: 'Optus', label: 'Optus' },
  { value: 'Cellcom', label: 'Cellcom' },
  { value: 'Google Fi', label: 'Google Fi' },
  { value: 'Republic', label: 'Republic' },
  { value: 'Consumer Cellular', label: 'Consumer Cellular' },
  { value: 'Xfinity', label: 'Xfinity' },
  { value: 'Spectrum', label: 'Spectrum' },
  { value: 'GCI', label: 'GCI' },
  { value: 'Ting', label: 'Ting' },
];

const EditProfileForm = ({ user }: { user: User }) => {
  const form = useForm<ProfileFormType>({
    initialValues: {
      firstName: user.first_name ?? '',
      lastName: user.last_name ?? '',
      email: user.email ?? '',
      phoneNumber: user.phone_number ?? '',
      address1: user.address1 ?? '',
      address2: user.address2 ?? '',
      city: user.city ?? '',
      state: user.state ?? '',
      zipCode: user.zip_code ?? '',
      country: user.country ?? '',
      mobileCarrier: user.phone_number_provider,
    },
    validate: {
      firstName: (value) =>
        value
          ? matches(REGEX.FIRST_NAME, 'First name is invalid')(value)
          : isNotEmpty('First name is required')(value),
      lastName: (value) =>
        value
          ? matches(REGEX.LAST_NAME, 'Last name is invalid')(value)
          : isNotEmpty('Last name is required')(value),
      phoneNumber: (value) =>
        value ? matches(REGEX.PHONE_NUMBER_REGEX, 'Phone Number is invalid')(value) : null,
      address1: (value) => (value ? matches(REGEX.ADDRESS1, 'Address is invalid')(value) : null),
      address2: (value) => (value ? matches(REGEX.ADDRESS2, 'Address is invalid')(value) : null),
      city: (value) => (value ? matches(REGEX.CITY, 'City is invalid')(value) : null),
      zipCode: (value) => (value ? matches(REGEX.ZIP_CODE, 'Zip code is invalid')(value) : null),
      country: (value) =>
        value ? matches(REGEX.US_ONLY_REGEX, 'Limited to customers in the US only')(value) : null,
    },
    validateInputOnBlur: true,
  });

  useFormListValidator({ form, id: 'profileForm' });

  return (
    <Stack>
      <Text fz={18} fw={600}>
        General Details
      </Text>
      <Grid grow gutter={8}>
        <Grid.Col span={6}>
          <TextInput placeholder="First Name" {...form.getInputProps('firstName')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput placeholder="Last Name" {...form.getInputProps('lastName')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput placeholder="Email" readOnly value={form.getInputProps('email').value} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            placeholder="Phone Number"
            {...form.getInputProps('phoneNumber')}
            value={phoneNumberDisplayFormat(form.values.phoneNumber)}
            onChange={(event) =>
              form.setFieldValue('phoneNumber', phoneNumberDisplayFormat(event.target.value))
            }
          />
        </Grid.Col>
      </Grid>
      <Select
        label="Mobile Carrier"
        data={mobileCarrierData}
        placeholder="Select carrier"
        description="By providing Givebacks with this information, you are giving your consent to receive text messages."
        inputWrapperOrder={['label', 'input', 'description']}
        searchable
        styles={{
          description: {
            fontSize: 'var(--mantine-font-size-xs)',
          },
        }}
        {...form.getInputProps('mobileCarrier')}
      />
      <Text fz={18} fw={600}>
        Address
      </Text>
      <Grid grow gutter={8}>
        <Grid.Col span={12}>
          <TextInput placeholder="Address, Line 1" {...form.getInputProps('address1')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput placeholder="Address, Line 2" {...form.getInputProps('address2')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput placeholder="City" {...form.getInputProps('city')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select.States {...form.getInputProps('state')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput placeholder="Zip" {...form.getInputProps('zipCode')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Select.Countries {...form.getInputProps('country')} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export { EditProfileForm };
